<template>
    <modal :show.sync=show modalClasses="medium-sized-modal calculator-modal" bodyClasses="p-0" :hide-footer="true" @close="resetForm()">
        <h3 slot="header" class="mb-0 font-weight-500">Legal Fees Calculator</h3>

        <div v-loading="loading" class="p-4">
            <div class="calculator-modal-tab mb-4" v-show="Object.keys(legalFees).length === 0">
                <b-tabs justified class="rounded-pill p-1">
                    <b-tab title-item-class="pr-1" title="Basic" @click="setCurrentTab('basic')" :active="form.tabState === 'basic'"></b-tab>
                    <b-tab title-item-class="pl-1" title="Advanced" @click="setCurrentTab('advance')" :active="form.tabState === 'advance'"></b-tab>
                </b-tabs>
            </div>

            <ValidationObserver v-slot="{ invalid }" ref="observer">
                <b-form ref="propertyIncomeForm" role="form" @submit.prevent="calculate()">
                    <div v-show="Object.keys(legalFees).length === 0">
                        <b-row>
                            <b-col cols="12" md="6">
                                <b-form-group label="Purchase Price">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'purchasePrice'"
                                    >
                                        <CurrencyInput
                                            v-model="form.purchase_price"
                                            :options="{ currency: 'MYR', currencyDisplay: 'hidden', autoDecimalDigits: true }"
                                        />
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="Loan Margin">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        :rules="{ required: true }"
                                        :vid="'loanMargin'"
                                    >
                                        <b-form-input
                                        v-model="form.loan_margin"
                                        type="text"
                                        maxlength="3"
                                        @keydown="preventNonNumericInput"
                                        required
                                        ></b-form-input>
                                        <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
                                    </ValidationProvider>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row v-if="form.tabState === 'advance'">
                            <b-col cols="12">
                                <b-form-group label="Developer">
                                    <b-form-radio-group
                                        id="radio-group-developer"
                                        v-model="form.project_type"
                                        name="Developer"
                                    >
                                        <b-form-radio :value="0">Developer</b-form-radio>
                                        <b-form-radio :value="1">Subsale</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" v-if="form.project_type === 1">
                                <b-form-group label="Title">
                                    <b-form-radio-group
                                        id="radio-group-title"
                                        v-model="form.title"
                                        name="Title"
                                    >
                                        <b-form-radio :value="0">Individual</b-form-radio>
                                        <b-form-radio :value="1">Master</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" v-if="form.project_type === 1 && form.title === 0">
                                <b-form-group label="Land Title">
                                    <b-form-radio-group
                                        id="radio-group-land-title"
                                        v-model="form.land_title"
                                        name="Land Title"
                                    >
                                        <b-form-radio :value="0">Leasehold</b-form-radio>
                                        <b-form-radio :value="1">Freehold</b-form-radio>
                                    </b-form-radio-group>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </div>

                    <LegalFeesCalculatorTable v-if="Object.keys(legalFees).length > 0" :legalFees="legalFees" />

                    <b-row>
                        <b-col md="6" sm="12" class="mx-auto text-center mt-4 pt-2">
                            <b-button type="submit" variant="primary" :disabled="invalid" v-show="Object.keys(legalFees).length === 0">Calculate</b-button>
                            <b-button variant="primary" @click.prevent="resetForm">Reset</b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </ValidationObserver>
        </div>
    </modal>
</template>
<script>
import Modal from '@/components/Modal'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import '@/assets/js/formValidations.js'
import CurrencyInput from '@/components/CurrencyInput'
import {
  notifyFunctionMixin,
  helperFunctionMixin,
  axios,
  services,
} from '@/assets/js/httpImports.js'
import LegalFeesCalculatorTable from '@/views/Calculators/CalculatorsTables/LegalFeesCalculatorTable'

export default {
    props: {
        legal_fees: Boolean
    },
    mixins:[ notifyFunctionMixin, helperFunctionMixin ],
    components: {
        Modal,
        ValidationObserver,
        ValidationProvider,
        CurrencyInput,
        LegalFeesCalculatorTable
    },
    data() {
        return {
            loading: false,
            form: {
                purchase_price: 500000,
                loan_margin: 90,
                project_type: 0,
                tabState: 'basic',
                title: 1,
                land_title: 0
            },
            legalFees: {},
        }
    },
    computed: {
        show: {
            get() {
                return this.legal_fees;
            },
            set(newVal) {
                this.$emit('update:legal_fees', newVal);
            },
        },
    },
    methods: {
        preventNonNumericInput(event) {
            const keyCode = event.keyCode || event.which;
            const keyValue = String.fromCharCode(keyCode);
            const currencyRegex = /^[\d.,$]+(\.[\d.,$]+)?$/;
            const allowedKeys = [8, 37, 38, 39, 40, 46];
            if (keyCode !== 190 && !currencyRegex.test(keyValue) && !allowedKeys.includes(keyCode)) {
                event.preventDefault();
            }
        },
        setCurrentTab(tabIndex) {
            this.form.tabState = tabIndex
        },
        calculate() {
            this.loading = true
            // Clone the original form object to avoid modifying it directly
            const calculator = { ...this.form }

            // Adjust the calculator object based on tabState and project_type
            if (this.form.tabState === 'basic') {
                // Exclude project_type, title and land_title
                delete calculator.project_type
                delete calculator.title
                delete calculator.land_title
            } else if (this.form.tabState === 'advance') {
                // Include project_type
                calculator.project_type = this.form.project_type

                if (this.form.project_type === 1) {
                    // Include title if project_type is 1
                    calculator.title = this.form.title

                    if (this.form.title === 0) {
                        // Include land_title if title is 0
                        calculator.land_title = this.form.land_title
                    } else {
                        // Exclude land_title if title is not 0
                        delete calculator.land_title
                    }
                } else {
                    // Exclude title and land_title if project_type is not 1
                    delete calculator.title
                    delete calculator.land_title
                }
            }

            let requestData = {
                calculator: calculator,
                client_token: 'clzNYY762WEHTP'
            }

            let url = `${services.fintos_calculator}legal-fees`
			axios.post(url, requestData)
			.then(res => {
				this.loading = false
                this.legalFees = res.data.data
			})
			.catch(e => {
				this.errorFunction(e)
			})
        },
        resetForm() {
            this.form = {
                purchase_price: 500000,
                loan_margin: 90,
                project_type: 0,
                tabState: 'basic',
                title: 1,
                land_title: 0
            }

            this.legalFees = {}
        }
    }
}
</script>